import { useEffect, useState, useCallback } from "react";
import {
    computeEqualSplits,
    isCustomValid,
    toggleInArray,
    toAmount,
    mapSplitDetailsToFormState,
    groupTransactionToEditState
} from "features/groups/lib/transactionSplits";
import { EditTransactionState } from "@/types/GroupTransaction";

/**
 * Maneja el estado local del form de edición de transacción,
 * incluyendo lógica de equal/custom split y validación.
 */
export function useEditGroupTransaction(transaction: EditTransactionState, people: string[] = []) {
    const [validationError, setValidationError] = useState("");
    const [editTransaction, setEditTransaction] = useState<EditTransactionState>(() => {
        // Use the utility function to convert from GroupTransaction format
        if (transaction?.splitDetails) {
            // Convert string amount to number for the utility function
            const transactionWithNumberAmount = {
                ...transaction,
                amount: typeof transaction.amount === 'string' ? parseFloat(transaction.amount) : transaction.amount
            };
            return groupTransactionToEditState(transactionWithNumberAmount);
        }
        
        // Fallback for existing EditTransactionState format
        const [splitBetween, customSplits] = mapSplitDetailsToFormState({});
        return {
            name: transaction?.name ?? "",
            amount: (transaction?.amount ?? 0).toString(),
            payer: transaction?.payer ?? "",
            splitType: transaction?.splitType ?? "equal",
            splitBetween: transaction?.splitBetween ?? splitBetween,
            customSplits: { ...(transaction?.customSplits ?? customSplits) },
            currency: transaction?.currency ?? "",
            type: transaction?.type,
            liquidityPoolId: transaction?.liquidityPoolId,
        };
    });

    // Recalcular splits iguales cuando cambia monto / selección / tipo
    useEffect(() => {
        if (editTransaction.splitType === "equal" && (editTransaction.splitBetween?.length ?? 0) > 0) {
            const equalSplits = computeEqualSplits(
                editTransaction.amount,
                editTransaction.splitBetween
            );
            setEditTransaction(prev => ({ ...prev, customSplits: equalSplits }));
        }
    }, [editTransaction.amount, editTransaction.splitBetween, editTransaction.splitType]);
    

    // Validar en modo custom que la suma coincida con el monto
    useEffect(() => {
        if (editTransaction.splitType === "custom") {
            const ok = isCustomValid(editTransaction.amount, editTransaction.customSplits);
            // Note: Error message should be translated in the component using this hook
            setValidationError(ok ? "" : "validation.splitsMustEqualAmount");
        } else {
            setValidationError("");
        }
    }, [editTransaction.customSplits, editTransaction.amount, editTransaction.splitType]);

    // Setters / handlers
    const setField = useCallback((key: keyof EditTransactionState, value: EditTransactionState[typeof key]) => {
        setEditTransaction((prev) => {
            const updates: Partial<EditTransactionState> = { [key]: value };
            // When switching to equal split, default to everyone
            if (key === "splitType" && value === "equal") {
                updates.splitBetween = people.length > 0 ? [...people] : prev.splitBetween ?? [];
            }
            return { ...prev, ...updates };
        });
    }, [people]);

    const handleSplitChange = useCallback((person: string, value: string | number) => {
        setEditTransaction(prev => ({
            ...prev,
            customSplits: {
                ...prev.customSplits,
                [person]: value
            }
        }));
    }, []);

    const onTogglePerson = useCallback((checked: boolean, person: string) => {
        setEditTransaction(prev => {
            const splitBetween = toggleInArray(prev.splitBetween, person, checked);
            return { ...prev, splitBetween };
        });
    }, []);

    const buildUpdatedTransaction = useCallback(() => {
        // Convert string|number values to numbers for splitDetails
        const splitDetails: Record<string, number> = {};
        for (const [key, value] of Object.entries(editTransaction.customSplits)) {
            splitDetails[key] = toAmount(value);
        }
        
        return {
            _id: transaction._id ?? '',
            name: editTransaction.name,
            amount: toAmount(editTransaction.amount),
            payer: editTransaction.payer,
            splitType: editTransaction.splitType,
            splitDetails: splitDetails,
            currency: editTransaction.currency,
            date: transaction.date ?? Date.now().toString(), // Preserve original date when editing
            category: editTransaction.category,
            type: editTransaction.type ?? transaction.type,
            liquidityPoolId: editTransaction.liquidityPoolId ?? transaction.liquidityPoolId,
        };
    }, [transaction, editTransaction]);

    return {
        editTransaction,
        validationError,
        setField,
        handleSplitChange,
        onTogglePerson,
        buildUpdatedTransaction
    };
}

